import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import FbButton from 'components/FbUI/FbButton'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { BulkEditSelectedRowsModal } from 'components/DataTable/BulkEditSelectedRowsModal'
import { bulkEditRows } from 'components/DataTable/bulkEditRows'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import ContactExportModal from 'components/Modals/ExportModal/ContactExportModal'
import dialogConfirm from 'components/dialogConfirm'
import { ContactResponse } from 'models/contacts'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMountEffect } from 'hooks/useMountEffect'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
  Ban,
  Cloud,
  Download,
  Mail,
  PencilLine,
  Tags,
  XCircle,
} from 'lucide-react'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import styled from 'styled-components'
import { FilterIdentifier } from '../../../models/saved_view'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from '../../Buttons/ActionsDropdown'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'
import { TableSearch } from '../../Filters/TableSearch'
import EditContactModal from '../../Modals/EditContactModal'
import * as S from '../CommonTable.styles'
import { BulkDeleteContactsModal } from 'components/Modals/BulkDeleteContactsModal/BulkDeleteContactsModal'
import ContactsFilterset from './ContactsFilterset'
import { useContactsTableColumns } from './useContactsTableColumns'
import { useBulkContactsDeleter } from './useBulkContactsDeleter'
import { SEQUENCE_SORTABLE_FIELDS } from './sequence/sequenceColumnInfo'
import SendEmailModal from 'components/Modals/SendEmailModal'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ContactCompany } from 'models/companies'
import {
  listCampaignColumnValues,
  recomputeCampaignColumn,
  CampaignColumnValue,
  COLUMNS_KEY,
  VALUES_KEY,
  editCampaignColumnValue,
  restoreCampaignColumnValue,
  upsertCampaignColumnValue,
} from 'services/campaignColumns'
import { useCampaignColumns } from 'features/campaigns/columns/useCampaignColumns'
import { buildAIColumnDefs } from 'features/campaigns/columns/buildAIColumnDefs'
import { AddAIColumnButton } from 'features/campaigns/columns/AddAIColumnButton'
import { campaignColumnHasPendingCompute } from 'features/campaigns/columns/campaignColumnProgress'
const tooltip = <Tooltip id="tooltip">Download limited to 10,000 rows</Tooltip>

type SortableFields = (keyof ContactResponse | string)[]

export type ContactsTableRef = {
  refetch: () => void
}

const BASE_SORTABLE_FIELDS: SortableFields = [
  'email',
  'id',
  'contact_company',
  'name',
  'title',
  'type',
  'url',
  'sales_stage',
  'account_owners',
  'contact_labels',
  'company_tags',
  'phone_number',
  'deal_count',
  'sales_stages',
  'company_size',
  'company_cuisine_50',
  'created',
  'modified',
  'is_do_not_contact',
  'date_last_sent',
  'date_last_received',
]

const PAGE_SIZE = 100
type CampaignColumnValuesResponse = { results: CampaignColumnValue[] }

interface ContactsTableProps {
  setTotalRowsCount?: (count: number) => void
  handleContactUpdated?: () => void
  forCompany?: Partial<ContactCompany>
  disableUrlPagination?: boolean
  disableExportButton?: boolean
  tableKey: string
  filterIdentifierModifier?: string
  baseFilters?: Record<string, any>
  forDoorProxyId?: number
  campaignId?: number
  campaignProductId?: number | null
}

function ContactsTableComponent(props: ContactsTableProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const IDENTIFIER = 'CONTACTS_TABLE' + (props.filterIdentifierModifier ?? '')
  const contactsFilterStore = filterStoreRepo.getStore(
    IDENTIFIER as FilterIdentifier
  )
  const { page, setPage } = contactsFilterStore()
  // Local-only — toggle resets on navigation, matching SamplesTable's
  // ``showInactive`` precedent. Promote to filterStore if persistence is needed.
  const [inSequenceOnly, setInSequenceOnly] = useState(false)

  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()

  const {
    state: { totalSelectedRows, rowSelection, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()
  const featureFlag = useFeatureFlagService()
  const campaignMode = !!props.campaignId && featureFlag.enableEmailSequencing
  // Gated by `enable_ai_custom_columns` (PostHog). Local dev: see
  // server/example_feature_flags.json. Before promoting beyond local, create
  // the flag in PostHog — flags are NOT seeded via Django fixtures anymore.
  const aiColumnsEnabled =
    !!props.campaignId && !!featureFlag.enableAiCustomColumns
  const sortableFields = useMemo(
    () =>
      campaignMode
        ? [...BASE_SORTABLE_FIELDS, ...SEQUENCE_SORTABLE_FIELDS]
        : BASE_SORTABLE_FIELDS,
    [campaignMode]
  )
  const [showExportModal, setShowExportModal] = useState(false)
  const [showAssignContactTags, setShowAssignContactTags] = useState(false)
  const [showAssignCompanyTags, setShowAssignCompanyTags] = useState(false)
  const [showBulkEditCustomColumn, setShowBulkEditCustomColumn] =
    useState(false)
  const [showEditContactModal, setShowEditContactModal] =
    React.useState<ContactResponse | null>(null)
  const [showWriteEmailForContactModal, setShowWriteEmailForContactModal] =
    React.useState<ContactResponse | null>(null)

  const filtersAsParams = {
    ...useFilterParams(contactsFilterStore),
    ...props.baseFilters,
  }

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filtersAsParams,
      ...sortParams,
      company: props.forCompany?.id,
      chain: props.forCompany?.chain,
      door_proxy_id: props.forDoorProxyId,
      ...(campaignMode ? { include_sequence_data: 'true' } : {}),
      ...(campaignMode && inSequenceOnly ? { in_sequence: 'true' } : {}),
    }),
    [
      filtersAsParams,
      sortParams,
      props.forCompany,
      props.forDoorProxyId,
      campaignMode,
      inSequenceOnly,
    ]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      const body = {
        ...filterAndSortParams,
        page: page,
        limit: PAGE_SIZE,
      }

      const contacts = await api.getContactsList(body, signal)
      return contacts
    },
  })

  // Sync count to parent + local state once per fetch result. Carrying the
  // setState calls inside ``queryFn`` re-fired on every background refetch
  // (window focus, polling, etc.); pulling them out keeps the query pure.
  // The setter identities are stable; only ``data?.count`` should retrigger.

  useEffect(() => {
    if (data?.count !== undefined) {
      props.setTotalRowsCount?.(data.count)
      setTotalRowsInBackend(data.count)
    }
  }, [data?.count])
  // END DATA FETCHING

  const bulkDelete = useBulkContactsDeleter({
    data: data?.results ?? [],
    tableQueryKey: TABLE_QUERY_KEY,
    filterParams: filterAndSortParams,
    forCompanyId: props.forCompany?.id,
    forDoorProxyId: props.forDoorProxyId,
    campaignId: props.campaignId,
  })

  // AI custom columns — only fetched when the flag is on and we have a campaign
  const { columns: aiColumns } = useCampaignColumns(props.campaignId ?? 0, {
    enabled: aiColumnsEnabled,
  })
  const isContactSelected = useCallback(
    (contact: ContactResponse) => Boolean(rowSelection[String(contact.id)]),
    [rowSelection]
  )
  const selectedPageContacts = useMemo(
    () => (data?.results ?? []).filter((contact) => isContactSelected(contact)),
    [data?.results, isContactSelected]
  )
  const selectedCustomColumnRowsAreLoaded =
    !isAllRowsSelected || selectedPageContacts.length === totalSelectedRows
  const bulkEditableCustomColumns = useMemo(
    () =>
      aiColumns.map((column) => ({
        id: column.id,
        label: column.name,
        helperText:
          column.status === 'PREVIEW'
            ? 'This preview column is not approved yet. Selected cells can still be manually overridden.'
            : undefined,
      })),
    [aiColumns]
  )

  // Track columns where a recompute was just kicked off, so we can:
  //  (1) show a spinner in the column header for visual feedback, and
  //  (2) keep polling alive even if the column was previously allComputed —
  //      otherwise updated values would never reach the UI without a refresh.
  const [recomputingColumnIds, setRecomputingColumnIds] = useState<Set<number>>(
    new Set()
  )

  // setTimeout handles for the recompute "Recomputing…" linger window. We
  // store them so unmount can clear pending timers — without this, the timer
  // fires against a torn-down component and React warns / leaks state.
  const recomputeTimeoutsRef = useRef<
    Map<number, ReturnType<typeof setTimeout>>
  >(new Map())
  useMountEffect(() => () => {
    recomputeTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
    recomputeTimeoutsRef.current.clear()
  })

  // Use useQueries so we can poll N columns without violating rules-of-hooks
  const valueQueries = useQueries({
    queries: aiColumnsEnabled
      ? aiColumns.map((c) => {
          const isRecomputing = recomputingColumnIds.has(c.id)
          const hasPendingCompute = campaignColumnHasPendingCompute(c)
          return {
            queryKey: VALUES_KEY(c.id),
            queryFn: () => listCampaignColumnValues(c.id),
            // Keep polling while compute is in flight, OR while our locally
            // fetched values haven't caught up to what the columns poll
            // already reports as computed. The columns poll fires every 5s;
            // without the catch-up clause, the columns query can flip to
            // "done" one cycle before the values poll has fetched the
            // final writes — preview cells render "—" until approve forces
            // a refetch by changing the status.
            refetchInterval: (query: {
              state: { data?: CampaignColumnValuesResponse }
            }) => {
              if (hasPendingCompute || isRecomputing) return 5000
              const fetchedCount = query.state.data?.results?.length ?? 0
              if (fetchedCount < c.progress.computed) return 5000
              return false as const
            },
          }
        })
      : [],
  })

  const valuesByColumn: Record<number, CampaignColumnValue[]> = {}
  aiColumns.forEach((c, i) => {
    valuesByColumn[c.id] = valueQueries[i]?.data?.results ?? []
  })

  const handleRecompute = async (columnId: number, includeEdited: boolean) => {
    setRecomputingColumnIds((prev) => {
      const next = new Set(prev)
      next.add(columnId)
      return next
    })
    try {
      // Call the API function directly rather than a per-column mutation hook
      // to avoid rules-of-hooks violations inside column-def callbacks.
      await recomputeCampaignColumn(columnId, { include_edited: includeEdited })
      if (props.campaignId) {
        queryClient.invalidateQueries({
          queryKey: COLUMNS_KEY(props.campaignId),
        })
      }
      queryClient.invalidateQueries({ queryKey: VALUES_KEY(columnId) })
    } finally {
      // Keep the "Recomputing…" label and value polling alive long enough to
      // catch the LLM call returning. Most agent runs land in 2-6s; 10s
      // covers slow paths without leaving stale "Recomputing…" labels around.
      // The handle is tracked so unmount can clear it — see useMountEffect.
      const existing = recomputeTimeoutsRef.current.get(columnId)
      if (existing) clearTimeout(existing)
      const timeoutId = setTimeout(() => {
        recomputeTimeoutsRef.current.delete(columnId)
        setRecomputingColumnIds((prev) => {
          if (!prev.has(columnId)) return prev
          const next = new Set(prev)
          next.delete(columnId)
          return next
        })
      }, 10000)
      recomputeTimeoutsRef.current.set(columnId, timeoutId)
    }
  }

  const handleBulkEditCustomColumn = async (
    columnId: number | string,
    value: string
  ) => {
    const numericColumnId = Number(columnId)
    const contactIds = selectedPageContacts.map((contact) => contact.id)
    if (!numericColumnId || contactIds.length === 0) return

    const result = await bulkEditRows(contactIds, (contactId) =>
      editCampaignColumnValue(numericColumnId, contactId, {
        new_value: value,
      })
    )
    queryClient.invalidateQueries({ queryKey: VALUES_KEY(numericColumnId) })
    if (props.campaignId) {
      queryClient.invalidateQueries({ queryKey: COLUMNS_KEY(props.campaignId) })
    }
    clearSelectedRows()
    if (result.failedCount > 0) {
      toast.warning(
        `Updated ${result.succeededCount} selected row${
          result.succeededCount === 1 ? '' : 's'
        }; ${result.failedCount} failed.`
      )
      return
    }

    toast.success(
      `Updated ${result.succeededCount} selected row${
        result.succeededCount === 1 ? '' : 's'
      }`
    )
  }

  const handleSaveCustomColumnValue = async (
    columnId: number,
    contactId: number,
    value: string
  ) => {
    const queryKey = VALUES_KEY(columnId)
    const previous = queryClient
      .getQueryData<CampaignColumnValuesResponse>(queryKey)
      ?.results.find((row) => row.contact === contactId)

    queryClient.setQueryData<CampaignColumnValuesResponse>(queryKey, (old) =>
      upsertCampaignColumnValue(old, {
        columnId,
        contactId,
        value,
      })
    )

    try {
      await editCampaignColumnValue(columnId, contactId, { new_value: value })
    } catch (err) {
      queryClient.setQueryData<CampaignColumnValuesResponse>(queryKey, (old) =>
        restoreCampaignColumnValue(old, contactId, previous)
      )
      throw err
    }
  }

  const handleCustomColumnSaveBatchSettled = (columnId: number) => {
    queryClient.invalidateQueries({ queryKey: VALUES_KEY(columnId) })
    if (props.campaignId) {
      queryClient.invalidateQueries({ queryKey: COLUMNS_KEY(props.campaignId) })
    }
  }

  const aiColumnDefs = aiColumnsEnabled
    ? buildAIColumnDefs(
        aiColumns,
        valuesByColumn,
        handleRecompute,
        handleSaveCustomColumnValue,
        handleCustomColumnSaveBatchSettled,
        props.campaignId ?? 0,
        recomputingColumnIds
      )
    : []

  const columns = [
    ...(useContactsTableColumns(campaignMode) as any),
    ...aiColumnDefs,
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      meta: {
        isEditable: false,
        headerDisableOrdering: true,
        isLastColumn: true,
        rightAlign: true,
      },
      cell: (info) => {
        const contact = info.row.original
        const contactId = contact.id
        const name = `${contact.first_name} ${contact.last_name}`

        return (
          <ActionsDropdown
            actions={[
              createEditAction(() => setShowEditContactModal(contact)),
              {
                label: 'Send Email',
                icon: Mail,
                onClick: () => setShowWriteEmailForContactModal(contact),
              },
              ...(featureFlag.enableEmailSequencing
                ? [
                    {
                      label: contact.is_do_not_contact
                        ? 'Remove from Do Not Contact'
                        : 'Mark as Do Not Contact',
                      icon: Ban,
                      onClick: () =>
                        handleContactDoNotContactToggle(
                          contactId,
                          name,
                          contact.is_do_not_contact
                        ),
                      hasSeparatorAfter: true,
                    },
                  ]
                : []),
              createDeleteAction(() => handleContactDelete(contactId, name)),
            ]}
          />
        )
      },
    },
  ] satisfies ColumnDef<ContactResponse, unknown>[]

  const handleContactDoNotContactToggle = async (
    contactId: number,
    name: string,
    isCurrentlyDnc: boolean
  ) => {
    const target = !isCurrentlyDnc
    if (target) {
      const confirmed = await dialogConfirm(
        <>
          Are you sure you want to mark <b>{name}</b> as Do Not Contact? This
          contact will be excluded from all campaign sequences.
        </>,
        'Mark as Do Not Contact?',
        '',
        { confirmLabel: 'Mark as Do Not Contact' }
      )
      if (!confirmed) return
    }
    const [err] = await to(
      api.bulkSetContactsDoNotContact({
        include_ids: [contactId],
        is_do_not_contact: target,
      })
    )
    if (err) {
      toast.error('Failed to update Do Not Contact status')
      return
    }
    toast.success(
      target
        ? `${name} marked as Do Not Contact`
        : `${name} removed from Do Not Contact list`
    )
    queryClient.invalidateQueries({
      queryKey: TABLE_QUERY_KEY,
      exact: false,
    })
  }

  const handleBulkMarkDoNotContact = async () => {
    const confirmed = await dialogConfirm(
      <>
        Are you sure you want to mark <b>{totalSelectedRows}</b> contact
        {totalSelectedRows === 1 ? '' : 's'} as Do Not Contact? They will be
        excluded from all campaign sequences.
      </>,
      'Mark as Do Not Contact?',
      '',
      { confirmLabel: 'Mark as Do Not Contact' }
    )
    if (!confirmed) return

    const [err, res] = await to(
      api.bulkSetContactsDoNotContact({
        include_ids: isAllRowsSelected
          ? []
          : (data?.results ?? [])
              .filter((contact) => isContactSelected(contact))
              .map((r) => r.id),
        exclude_ids: isAllRowsSelected
          ? (data?.results ?? [])
              .filter((contact) => !isContactSelected(contact))
              .map((r) => r.id)
          : [],
        filter_params: filterAndSortParams,
        is_do_not_contact: true,
      })
    )
    if (err) {
      toast.error('Failed to mark contacts as Do Not Contact')
      return
    }
    toast.success(res?.message ?? 'Selected contacts marked as Do Not Contact')
    clearSelectedRows()
    queryClient.invalidateQueries({
      queryKey: TABLE_QUERY_KEY,
      exact: false,
    })
  }

  const handleContactDelete = async (contactId: number, name: string) => {
    if (
      await dialogConfirm(
        <>
          Are you sure you want to delete <b>{name}</b>? This action cannot be
          undone.
        </>,
        'Delete Contact?',
        '',
        { confirmLabel: 'Delete' }
      )
    ) {
      const [err] = await to(api.deleteContact(contactId))
      if (!err) {
        toast.success('Contact deleted successfully')
        refetch()
        if (props.forCompany?.id) {
          queryClient.invalidateQueries({
            queryKey: ['company', props.forCompany.id],
          })
        }
        if (props.forDoorProxyId) {
          queryClient.invalidateQueries({
            queryKey: ['door_proxy', props.forDoorProxyId],
          })
        }
      }
    }
  }

  return (
    <DataTableContainer>
      <TagAssignModal
        show={showAssignContactTags}
        tagParent="contacts"
        handleClose={() => setShowAssignContactTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="tags"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'contacts-table',
            isContactTags: true,
          })
        }}
        idAccessor="id"
      />

      <TagAssignModal
        show={showAssignCompanyTags}
        tagParent="products"
        handleClose={() => setShowAssignCompanyTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="contact_company.taglist"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'contacts-table',
          })
        }}
        idAccessor="contact_company.id"
      />

      {showEditContactModal && (
        <EditContactModal
          show={!!showEditContactModal}
          handleClose={() => setShowEditContactModal(null)}
          onContactUpdated={props.handleContactUpdated}
          contact={showEditContactModal}
          forCompany={props.forCompany}
          initialDoorIds={props.forDoorProxyId ? [props.forDoorProxyId] : []}
        />
      )}

      {showWriteEmailForContactModal && (
        <SendEmailModal
          show={!!showWriteEmailForContactModal}
          handleClose={() => setShowWriteEmailForContactModal(null)}
          contact={showWriteEmailForContactModal}
        />
      )}

      <ContactExportModal
        tableTypeSlug="contacts-table"
        count={data?.count ?? 0}
        show={showExportModal}
        _handleClose={() => setShowExportModal(false)}
        filters={filterAndSortParams}
      />

      {bulkDelete.showModal && (
        <BulkDeleteContactsModal
          show={bulkDelete.showModal}
          onClose={bulkDelete.closeModal}
          onConfirm={bulkDelete.confirmDelete}
          isDeleting={bulkDelete.isDeleting}
          count={bulkDelete.count}
          contactIds={bulkDelete.pageVisibleIds}
          isAllRowsSelected={bulkDelete.isAllRowsSelected}
          filterParams={filterAndSortParams}
        />
      )}

      {showBulkEditCustomColumn && (
        <BulkEditSelectedRowsModal
          show={showBulkEditCustomColumn}
          onClose={() => setShowBulkEditCustomColumn(false)}
          columns={bulkEditableCustomColumns}
          selectedRowsCount={selectedPageContacts.length}
          onApply={handleBulkEditCustomColumn}
        />
      )}

      <DataTable
        key={props.tableKey}
        tableHeader={
          <S.SearchContainer>
            <S.SearchContainerFilters>
              <S.TableSearchContainer>
                <TableSearch
                  filterStore={contactsFilterStore}
                  searchPlaceholder="Search by name, company, company domain, email..."
                  tableKey={props.tableKey}
                />
              </S.TableSearchContainer>
              <S.TableButtonsContainer>
                <ContactsFilterset
                  filterIdentifier={IDENTIFIER as FilterIdentifier}
                  campaignMode={campaignMode}
                />
                <ColumnSelectorRecipient tableKey={props.tableKey} />
                {aiColumnsEnabled && props.campaignId && (
                  <AddAIColumnButton
                    campaignId={props.campaignId}
                    campaignProductId={props.campaignProductId}
                  />
                )}
                {campaignMode && (
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={inSequenceOnly}
                      onChange={(e) => {
                        setInSequenceOnly(e.target.checked)
                        // Toggle changes the result set size; without a reset
                        // a user on page 3 can land on an empty page.
                        setPage(1)
                      }}
                      className="h-4 w-4 rounded border-line accent-brand focus:ring-2 focus:ring-brand"
                    />
                    <span className="text-sm font-medium text-fg-secondary">
                      In sequence
                    </span>
                  </label>
                )}
              </S.TableButtonsContainer>
            </S.SearchContainerFilters>
            <div className="flex flex-row justify-between gap-4">
              <TableButtonAction
                items={[
                  {
                    label: 'Manage Contact Labels',
                    icon: <Tags size={20} />,
                    callback: () => setShowAssignContactTags(true),
                  },
                  {
                    label: 'Manage Company Tags',
                    icon: <Tags size={20} />,
                    callback: () => setShowAssignCompanyTags(true),
                  },
                  {
                    label: 'Push to Salesforce',
                    icon: <Cloud size={20} />,
                    hidden: !featureFlag.enablePushToSalesforce,
                    callback: () => {
                      setTimeout(() => {
                        toast.success('Successfully pushed to Salesforce')
                      }, 1000)
                    },
                  },
                  {
                    label: 'Mark as Do Not Contact',
                    icon: <Ban size={20} />,
                    callback: handleBulkMarkDoNotContact,
                    hidden: !featureFlag.enableEmailSequencing,
                  },
                  {
                    label: 'Bulk Edit Custom Column',
                    icon: <PencilLine size={20} />,
                    callback: () => setShowBulkEditCustomColumn(true),
                    hidden: !aiColumnsEnabled,
                    disabled:
                      !selectedCustomColumnRowsAreLoaded ||
                      bulkEditableCustomColumns.length === 0 ||
                      selectedPageContacts.length === 0,
                    disabledTooltipText: !selectedCustomColumnRowsAreLoaded
                      ? 'Bulk edit custom columns only supports rows loaded on this page. Select rows on this page before applying one value.'
                      : bulkEditableCustomColumns.length === 0
                        ? 'Add a custom column before bulk editing values.'
                        : 'Select rows on this page to bulk edit custom column values.',
                  },
                  {
                    label: 'Delete Contacts',
                    icon: <XCircle size={20} />,
                    callback: () => bulkDelete.openModal(),
                  },
                ]}
                disabled={!totalSelectedRows}
                selectedRowsCount={totalSelectedRows ?? 0}
              />
            </div>
            <FilterChips
              identifier={'CONTACTS_TABLE'}
              store={contactsFilterStore}
              clearAllButton
              showActive
            />
          </S.SearchContainer>
        }
        tableKey={props.tableKey}
        loading={isFetching}
        data={data?.results ?? []}
        columns={columns}
        getRowId={(contact) => String(contact.id)}
        sortableFields={sortableFields}
        enableRowSelection
        virtualizeRows={false}
        isPaginationEnabled={true}
        stickyLastColumn
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText={'Select Contacts'}
        footerControls={
          !props.disableExportButton ? (
            <OverlayTrigger placement="top" overlay={tooltip}>
              <ExportButton
                variant="secondary"
                onClick={() => setShowExportModal(true)}
              >
                <Download size={18} />
              </ExportButton>
            </OverlayTrigger>
          ) : undefined
        }
      />
    </DataTableContainer>
  )
}

ContactsTableComponent.displayName = 'ContactsTable'

export function ContactsTable(
  props: ContactsTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const {
    tableKey,
    defaultColumnVisibility,
    campaignId,
    campaignProductId,
    ...rest
  } = props

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={
        campaignId
          ? [
              'Sequencing',
              'Custom columns',
              'Contact Information',
              'Activity',
              'Account Details',
            ]
          : ['Contact Information', 'Sequencing', 'Activity', 'Account Details']
      }
    >
      <DataTableProvider tableKey={tableKey}>
        <ContactsTableComponent
          tableKey={tableKey}
          campaignId={campaignId}
          campaignProductId={campaignProductId}
          {...rest}
        />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}

const ExportButton = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
